import { useState } from "react";
import { X, ChevronLeft, ChevronRight, Users, Maximize2, Eye, Wifi, Flame, UtensilsCrossed, Car, Bath, BedDouble } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useInView } from "@/hooks/useInView";
import { translations } from "@/data/translations";

const AMENITY_ICONS = {
  "Wi-Fi": Wifi,
  "Piscinas Aquecidas": Flame,
  "Heated Pools": Flame,
  "BBQ Privado": UtensilsCrossed,
  "Private BBQ": UtensilsCrossed,
  "Cozinha Exterior": UtensilsCrossed,
  "Outdoor Kitchen": UtensilsCrossed,
  "Casa de Banho Privada": Bath,
  "Private Bathroom": Bath,
  "Estacionamento": Car,
  "Parking": Car,
  "Roupa de Cama": BedDouble,
  "Bed Linen": BedDouble,
};

function AccommodationModal({ item, t, onClose }) {
  const [imgIndex, setImgIndex] = useState(0);
  const allImages = [item.mainImage, ...item.images];

  const prevImg = () => setImgIndex((i) => (i - 1 + allImages.length) % allImages.length);
  const nextImg = () => setImgIndex((i) => (i + 1) % allImages.length);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="accommodation-modal"
    >
      <div className="bg-white rounded-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="relative aspect-[16/9] overflow-hidden rounded-t-2xl">
          <img
            src={allImages[imgIndex]}
            alt={item.name}
            className="w-full h-full object-cover transition-opacity duration-300"
          />
          <button
            onClick={prevImg}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
            data-testid="modal-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={nextImg}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
            data-testid="modal-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="absolute top-3 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm text-white flex items-center justify-center hover:bg-black/60 transition-colors"
            data-testid="modal-close"
          >
            <X className="w-4 h-4" />
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
            {allImages.map((_, i) => (
              <button
                key={i}
                onClick={() => setImgIndex(i)}
                className={`rounded-full transition-all duration-200 ${i === imgIndex ? "w-5 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/50"}`}
              />
            ))}
          </div>
        </div>
        <div className="p-6 md:p-8">
          <div className="flex items-start justify-between mb-2">
            <h3 className="font-playfair text-2xl md:text-3xl font-medium text-[#1F2937]">{item.name}</h3>
          </div>
          <p className="text-[#D4A373] font-manrope text-sm font-medium mb-4">{item.tagline}</p>
          <div className="flex gap-5 text-sm text-[#6B7280] font-manrope mb-5">
            <span className="flex items-center gap-1.5"><Users className="w-4 h-4" /> {item.guests} {t("accommodations.guests")}</span>
            <span className="flex items-center gap-1.5"><Maximize2 className="w-4 h-4" /> {item.size}</span>
            <span className="flex items-center gap-1.5"><Eye className="w-4 h-4" /> {item.view}</span>
          </div>
          <p className="text-sm font-manrope font-light text-[#4B5563] leading-relaxed mb-6">{item.description}</p>
          <div className="mb-6">
            <p className="text-xs uppercase tracking-widest font-bold text-[#6B7280] mb-3 font-manrope">{t("accommodations.amenities")}</p>
            <div className="flex flex-wrap gap-2">
              {item.amenities.map((a) => {
                const Icon = AMENITY_ICONS[a] || Wifi;
                return (
                  <span key={a} className="flex items-center gap-1.5 px-3 py-1.5 bg-[#F3F4F1] rounded-full text-xs font-manrope text-[#4B5563]">
                    <Icon className="w-3 h-3 text-[#3A4D39]" /> {a}
                  </span>
                );
              })}
            </div>
          </div>
          <a
            href={item.bookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="block text-center rounded-full px-8 py-3.5 bg-[#3A4D39] text-white font-medium hover:bg-[#2C3E2B] transition-all duration-300 font-manrope shadow-md"
            data-testid={`modal-book-${item.id}`}
          >
            {t("accommodations.book")} — {item.name}
          </a>
        </div>
      </div>
    </div>
  );
}

function AccommodationCard({ item, t, index, onOpen }) {
  const isVintage = item.id === "vintage-ford";

  return (
    <div
      className={`group relative overflow-hidden rounded-2xl cursor-pointer transition-all duration-500 hover:shadow-2xl hover:-translate-y-1 ${
        isVintage ? "border-2 border-[#D4A373]/40 bg-[#FDF8F0]" : "bg-white border border-stone-100"
      }`}
      onClick={() => onOpen(item)}
      data-testid={`accommodation-card-${item.id}`}
    >
      <div className="relative aspect-[4/3] overflow-hidden">
        <img
          src={item.mainImage}
          alt={item.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        {isVintage && (
          <div className="absolute top-3 right-3 px-3 py-1 bg-[#D4A373] rounded-full text-white text-xs font-bold font-manrope uppercase tracking-wide">
            Vintage
          </div>
        )}
      </div>
      <div className="p-5 md:p-6">
        <p className="text-xs uppercase tracking-[0.15em] font-bold text-[#D4A373] mb-1.5 font-manrope">{item.tagline}</p>
        <h3 className="font-playfair text-xl md:text-2xl font-medium text-[#1F2937] mb-2">{item.name}</h3>
        <div className="flex gap-4 text-xs text-[#6B7280] font-manrope mb-4">
          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {item.guests} {t("accommodations.guests")}</span>
          <span className="flex items-center gap-1"><Maximize2 className="w-3.5 h-3.5" /> {item.size}</span>
        </div>
        <p className="text-sm font-manrope font-light text-[#6B7280] line-clamp-2 mb-5">{item.description}</p>
        <div className="flex gap-2">
          <button
            className="flex-1 text-center rounded-full px-4 py-2.5 bg-[#3A4D39] text-white text-sm font-medium hover:bg-[#2C3E2B] transition-colors font-manrope"
            data-testid={`card-moreinfo-${item.id}`}
            onClick={(e) => { e.stopPropagation(); onOpen(item); }}
          >
            {t("accommodations.moreInfo")}
          </button>
          <a
            href={item.bookUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 text-center rounded-full px-4 py-2.5 border border-[#3A4D39] text-[#3A4D39] text-sm font-medium hover:bg-[#3A4D39] hover:text-white transition-all duration-200 font-manrope"
            data-testid={`card-book-${item.id}`}
            onClick={(e) => e.stopPropagation()}
          >
            {t("accommodations.book")}
          </a>
        </div>
      </div>
    </div>
  );
}

export default function Accommodations() {
  const { t, lang } = useLanguage();
  const [ref, inView] = useInView();
  const [activeModal, setActiveModal] = useState(null);

  const items = translations[lang]?.accommodations?.items || [];

  return (
    <section id="accommodations" className="py-24 md:py-32 bg-[#F3F4F1]" data-testid="accommodations-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div
          ref={ref}
          className={`transition-all duration-700 ${inView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}
        >
          <div className="flex items-center gap-3 mb-6">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#3A4D39] font-manrope">
              {t("accommodations.badge")}
            </span>
            <div className="flex-1 h-px bg-[#3A4D39]/20" />
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-14">
            <div>
              <h2 className="font-playfair text-4xl md:text-5xl font-normal text-[#1F2937] leading-tight">
                {t("accommodations.title")}
              </h2>
              <p className="text-base font-manrope font-light text-[#6B7280] mt-3 max-w-lg">
                {t("accommodations.subtitle")}
              </p>
            </div>
            <a
              href="https://book.yourholiday.pt/en/search-availability/"
              target="_blank"
              rel="noopener noreferrer"
              className="shrink-0 rounded-full px-7 py-3 bg-[#D4A373] text-white font-medium hover:bg-[#C29262] transition-colors font-manrope text-sm shadow-md"
              data-testid="accommodations-book-all"
            >
              {t("nav.book")}
            </a>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6">
            {items.map((item, i) => (
              <AccommodationCard
                key={item.id}
                item={item}
                t={t}
                index={i}
                onOpen={setActiveModal}
              />
            ))}
          </div>
        </div>
      </div>

      {activeModal && (
        <AccommodationModal item={activeModal} t={t} onClose={() => setActiveModal(null)} />
      )}
    </section>
  );
}
